import {
  AddIcon,
  Alert,
  Button,
  CardResource,
  CloseIcon,
  IconButton,
  List,
  ListItem,
  SaveIcon,
  Skeleton,
  Stack,
  TaskItem,
  TextField,
  Typography,
} from '@all-manager/design-system-v2';
import TaskMenuActions from "./TaskMenuActions";
import { useTodoSection } from "./useTodoSection";

type Props = {
  subTopicId: string;
};

const SKELETON_ROWS = [0, 1, 2];

export default function TodoSection(props: Props) {
  const hook = useTodoSection(props.subTopicId);
  const totalTasks = hook.tasks.length;

  return (
    <CardResource
      variant="task"
      title="À faire"
      subheader={
        totalTasks === 0
          ? 'Actions à réaliser'
          : `Actions à réaliser · ${hook.completedCount} sur ${totalTasks} terminée${hook.completedCount > 1 ? 's' : ''}`
      }
    >
      <Stack spacing={2}>
        {hook.isAdmin ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            fullWidth
            size="small"
            label="Nouvelle tâche"
            placeholder="Relancer le syndic, vérifier la facture…"
            value={hook.newTaskTitle}
            onChange={(event) => hook.setNewTaskTitle(event.target.value)}
            onKeyDown={hook.handleNewTaskKeyDown}
          />
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon fontSize="small" />}
            onClick={hook.handleAddTask}
            disabled={!hook.newTaskTitle.trim()}
            loading={hook.isAdding}
          >
            Ajouter
          </Button>
        </Stack>
        ) : null}

        {hook.isError ? (
          <Alert severity="error">Les tâches n&apos;ont pas pu être chargées. Rechargez la page pour réessayer.</Alert>
        ) : hook.isLoading ? (
          <Stack spacing={1} aria-busy="true" aria-label="Chargement des tâches">
            {SKELETON_ROWS.map((row) => (
              <Skeleton key={row} variant="text" height={32} />
            ))}
          </Stack>
        ) : totalTasks === 0 ? (
          <Typography variant="noItem">
            {hook.isAdmin ? 'Aucune tâche pour le moment. Saisissez la prochaine action à réaliser ci-dessus.' : 'Aucune tâche pour le moment.'}
          </Typography>
        ) : (
          <List dense disablePadding aria-label="Tâches">
            {hook.tasks.map((task) => (
              hook.editingTaskId === task.id ? (
                <ListItem key={task.id} disableGutters>
                  <TextField
                    fullWidth
                    size="small"
                    label="Modifier la tâche"
                    value={hook.editingTitle}
                    onChange={(event) => hook.setEditingTitle(event.target.value)}
                    onKeyDown={(event) => hook.handleEditKeyDown(task.id, event)}
                    autoFocus
                  />
                  <IconButton
                    color="primary"
                    tooltip="Enregistrer la tâche"
                    onClick={() => hook.handleSaveEdit(task.id)}
                    disabled={!hook.editingTitle.trim()}
                  >
                    <SaveIcon fontSize="small" />
                  </IconButton>
                  <IconButton tooltip="Annuler la modification" onClick={hook.handleCancelEdit}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </ListItem>
              ) : (
                <TaskItem
                  key={task.id}
                  task={task}
                  checked={task.state === 'completed'}
                  onChange={(completed) => hook.toggleTask({ id: task.id, completed })}
                  disabled={!hook.isAdmin}
                  action={hook.isAdmin ? (
                    <TaskMenuActions
                      task={task}
                      onEdit={() => hook.handleStartEdit(task.id, task.title)}
                      onDelete={() => hook.deleteTask(task.id)}
                    />
                  ) : undefined}
                />
              )
            ))}
          </List>
        )}
      </Stack>
    </CardResource>
  );
}
